import React, { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { dataTrigger } from "@/analytics";
import { fetchAppFeatures } from "@/api/app";
import { loginWithEmail, resendVerification } from "@/api/auth";
import { EmailCodeStep } from "./EmailCodeStep";
import { useAuth } from "@/store/authStore";
import { Button } from "@/ui/Button/Button";
import cls from "./styles.module.scss";

interface EmailLoginFormProps {
  onSwitchToRegister: () => void;
  /** Идёт вход по коду из письма (true) или обычная форма (false). */
  onCodeStep?: (on: boolean) => void;
}

export function EmailLoginForm({ onSwitchToRegister, onCodeStep }: EmailLoginFormProps) {
  const navigate = useNavigate();
  const setTokens = useAuth((s) => s.setTokens);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailCode, setEmailCode] = useState(false);
  // «Войти по коду из письма»: ask — спросить почту, sent — письмо ушло, ввод кода
  const [codeMode, setCodeMode] = useState<"off" | "ask" | "sent">("off");

  useEffect(() => {
    void fetchAppFeatures().then((f) => setEmailCode(Boolean(f.email_code)));
  }, []);

  const isEmailValid = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email), [
    email,
  ]);

  const canSubmit = isEmailValid && password.length >= 6 && !loading;

  const onSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();
    setError("");

    if (!email) return setError("Введите email");
    if (!isEmailValid) return setError("Введите корректный email");
    if (!password) return setError("Введите пароль");
    if (password.length < 6) {
      return setError("Пароль должен быть минимум 6 символов");
    }

    try {
      setLoading(true);
      const tokens = await loginWithEmail(email, password);
      setTokens(tokens.access, tokens.refresh, "email");
      navigate("/");
    } catch {
      setError("Ошибка входа. Проверьте данные и попробуйте снова.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    onCodeStep?.(codeMode !== "off");
  }, [codeMode, onCodeStep]);

  const sendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEmailValid) return setError("Введите корректный email");
    setError("");
    try {
      setLoading(true);
      await resendVerification(email.trim().toLowerCase());
      setCodeMode("sent");
    } catch (err) {
      const data = (err as { response?: { data?: { error?: string } } })?.response?.data;
      setError(data?.error || "Не удалось отправить письмо. Попробуйте позже.");
    } finally {
      setLoading(false);
    }
  };

  if (codeMode === "sent") {
    return <EmailCodeStep email={email.trim().toLowerCase()} purpose="login" onChangeEmail={() => setCodeMode("ask")} />;
  }

  if (codeMode === "ask") {
    return (
      <form onSubmit={sendCode} className={cls.form}>
        <p className={cls.codeText}>Пришлём на почту код — по нему войдёте без пароля. Пароль не изменится.</p>
        <div className={cls.field}>
          <label className={cls.label}>Email</label>
          <input
            className={cls.input}
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError("");
            }}
            placeholder="example@mail.com"
            autoComplete="email"
            autoFocus
          />
        </div>
        {error ? <div className={cls.error}>{error}</div> : null}
        <Button
          variant="primary"
          size="lg"
          type="submit"
          disabled={!isEmailValid || loading}
          aria-busy={loading}
          leftIcon={loading ? <Loader2 className={cls.loaderIcon} /> : undefined}
          {...dataTrigger("bkn.auth.login_by_code_send")}
        >
          Прислать код
        </Button>
        <div className={cls.switchRow}>
          <button type="button" className={cls.linkBtn} onClick={() => setCodeMode("off")}>
            Войти с паролем
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={onSubmit} className={cls.form}>
      <div className={cls.field}>
        <label className={cls.label}>Email</label>
        <input
          className={cls.input}
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError("");
          }}
          placeholder="example@mail.com"
          autoComplete="email"
        />
      </div>

      <div className={cls.field}>
        <label className={cls.label}>Пароль</label>
        <input
          className={cls.input}
          type="password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setError("");
          }}
          placeholder="••••••••"
          autoComplete="current-password"
        />
      </div>

      {error ? <div className={cls.error}>{error}</div> : null}

      <Button
        variant="primary"
        size="lg"
        type="submit"
        disabled={!canSubmit}
        aria-busy={loading}
        leftIcon={loading ? <Loader2 className={cls.loaderIcon} /> : undefined}
        {...dataTrigger("bkn.auth.login")}
      >
        {loading ? "Подождите..." : "Войти"}
      </Button>

      {emailCode ? (
        <div className={cls.switchRow}>
          <button
            type="button"
            className={cls.linkBtn}
            onClick={() => {
              setError("");
              setCodeMode("ask");
            }}
            {...dataTrigger("bkn.auth.login_by_code")}
          >
            Забыли пароль? Войти по коду из письма
          </button>
        </div>
      ) : null}

      <div className={cls.switchRow}>
        <button
          type="button"
          className={cls.linkBtn}
          onClick={onSwitchToRegister}
          {...dataTrigger("bkn.auth.to_register")}
        >
          Нет аккаунта? Регистрация
        </button>
      </div>
    </form>
  );
}
